<template>
  <transition tag="div" name="modal">
    <div
      class="modals"
      v-show="isActive"
      tabindex="0"
      @keyup.esc.prevent="handleEscapeKey($event)"
      @click="handleBackdropClick()"
    >
      <transition-group
        class="modals__wrap"
        tag="div"
        name="modal-item"
        origin="center center"
      >
        <div
          class="modal"
          v-for="(modal, idx) in modals"
          :key="modal.key + idx"
          :class="getCssClasses(idx)"
        >
          <div class="modal-card" @click.stop>
            <button
              @click="dismiss()"
              v-if="modal.closable"
              class="modal__close"
            >
              <SvgIcon size="13" name="close" />
            </button>
            <component
              :is="modal.component"
              v-bind="modal.props"
              ref="components"
            ></component>
          </div>
        </div>
      </transition-group>
    </div>
  </transition>
</template>

<script>
import Bus from "./bus";
import Simplebar from "simplebar-vue";

export default {
  name: "modals",
  components: { Simplebar },
  provide() {
    return {
      modalsGroup: this
    };
  },
  data() {
    return {
      isActive: false,
      modals: []
    };
  },

  computed: {
    current() {
      return this.modals[this.$last];
    },
    $last() {
      return this.modals.length - 1;
    },
    html() {
      if (typeof document !== "undefined") {
        return document.querySelector("html");
      }
      return null;
    }
  },

  watch: {
    "modals.length"(number) {
      this.isActive = number > 0;
      if (number > 0) {
        this.html.classList.add("modal-open");
      } else {
        this.html.classList.remove("modal-open");
      }
    }
  },

  created() {
    Bus.$on("new", this.new);
    Bus.$on("close", this.close);
    Bus.$on("dismiss", this.dismiss);
  },

  destroyed() {
    Bus.$off("new", this.new);
    Bus.$off("close", this.close);
    Bus.$off("dismiss", this.dismiss);
  },

  methods: {
    triggerResize() {
      this.$nextTick(() => {
        const resizeEvent = document.createEvent("UIEvents");
        resizeEvent.initUIEvent("resize", true, false, window, 0);
        window.dispatchEvent(resizeEvent);
      });
    },

    new(options) {
      const defaults = {
        key: Date.now(),
        title: null,
        dismissable: true,
        closable: true,
        name: "",
        escapable: false,
        closeOnBackdrop: true,
        onClose() {},
        onDismiss() {}
      };
      options = Object.assign(defaults, options);

      this.modals.push(options);
      Bus.$emit("opened", {
        index: this.$last,
        options
      });

      document.querySelector(".modals").scrollTop = 0;
      this.triggerResize();

      if (
        document.activeElement &&
        document.activeElement.tagName !== "BODY" &&
        document.activeElement.blur
      ) {
        document.activeElement.blur();
      }
    },

    splice(index = null) {
      if (index === -1) return;
      if (!this.modals.length) return;
      if (index === null) this.modals.splice(-1, 1);
      else this.modals.splice(index, 1);
      // If it was the last window, notify that all instances are destroyed
      if (!this.modals.length) {
        Bus.$emit("destroyed");
      }
    },

    doClose(data = null, index) {
      // If there's nothing to close, ignore it
      try {
        if (!this.modals.length) return;
        if (!this.modals[index]) return;
        this.splice(index);
        // Firefox fix: https://github.com/javisperez/modals/issues/1
        const modals = document.querySelector(".modals");
        if (modals) modals.scrollTop = 0;
      } catch (e) {
        console.warn("modals doClose exception:", e, data);
      }
    },

    getIndexByComponent(component) {
      if (component._isVue) {
        const modals = Array.from(this.$refs.components.entries());
        const pair = modals.find(item => item[1] === component);
        return pair ? pair[0] : -1;
      } else {
        return this.modals.findIndex(
          ({ component: item }) => item && item.name === component.name
        );
      }
    },

    getIndex({ index = -1, component = null }) {
      if (component) index = this.getIndexByComponent(component);
      if (index === -1) index = this.$last;
      return index;
    },

    close(data = {}) {
      if (this.modals.length === 0) return;
      const index = this.getIndex(data);
      const modal = this.modals[index];

      modal.onClose(data);
      this.doClose(data, index);

      Bus.$emit("closed", {
        index,
        instance: modal,
        data
      });
    },

    dismiss(index = this.$last) {
      // Can't dismiss modal if there's no modal open
      if (this.modals.length === 0) return;

      const modal = this.modals[index];

      if (!modal.dismissable) return;

      // Check dismiss callback result for prevention
      if (modal.onDismiss() === false) return;

      this.doClose(null, index);

      Bus.$emit("dismissed", {
        index,
        instance: modal
      });
    },

    getCssClasses(index) {
      const modal = this.modals[index];
      let classNames = modal.name;
      if (index < this.$last) classNames += " disabled";
      return classNames;
    },

    handleEscapeKey() {
      if (!this.modals.length) return;
      if (this.current.escapable) this.dismiss();
    },

    handleBackdropClick() {
      if (!this.modals.length) {
        return;
      }
      if (this.current.closeOnBackdrop === true) {
        this.dismiss();
      }
    }
  }
};
</script>

<style lang="scss">
html.modal-open {
  overflow: hidden;
  height: 100%;
}
.modals {
  background-color: rgba(#000a25, 0.6);
  position: fixed;
  left: 0;
  right: 0;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: auto;
  perspective: 500px;

  /* transition-property: opacity; */
  top: 0;
  min-height: 100%;

  &:focus {
    outline: none;
  }
}

.modals__wrap {
  background: transparent;
  box-shadow: none;
  /* transition: 0.6s ease; */
  /* transition-property: transform, opacity; */
  position: absolute;
  height: 100vh;
  width: 100vw;

  /* left: 50%;
  transform: translateX(-50%);
  will-change: transform;
  width: 100%;
  height: 100%; */

  .simplebar-content {
    height: 100%;
    position: relative;
  }

  .simplebar-scrollbar {
    width: 4px;

    .simplebar-track.simplebar-horizontal & {
      height: 4px;
    }

    &:before {
      border-radius: 100px;
      background: rgba(#fff, 0.5);
    }
  }
}

.modal {
  margin: 0 auto;
  padding: 55px 0 30px;
  width: 100%;
  max-width: 80%;
  box-sizing: border-box;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition: 0.4s ease;
  transition-property: transform, opacity;
  will-change: transform, opacity;
  /* transition: all 0.6s ease; */

  display: flex;
  align-items: center;
  min-height: 100%;

  &__close {
    position: absolute;
    z-index: 1;
    right: 22px;
    top: 22px;
    transition: transform 0.3s;
    transform-origin: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
      transform: rotate(180deg);
    }
  }

  &.disabled {
    opacity: 0.2;
    max-height: 100%;
    overflow: hidden;
    flex-wrap: wrap;

    &::after {
      background: transparent;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100;
    }
  }

  &-item-enter-active,
  &-item-leave-active {
    transition: 0.4s ease;
  }
  &-item-enter,
  &-item-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-30px) scale(0.95);
  }

  &-enter-active,
  &-leave-active {
    transition: 0.4s ease;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
}

/* .modal-open {
  .application_wrap {
    @include filter-blur(6);
  }
} */

.modal-card {
  background: gradient(#151a30, #21376f);
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.5);
  border-radius: 6px;
  padding: 40px;
  max-width: 700px;
  min-height: 120px;
  margin: 0 auto;
  position: relative;

  .title {
    font-weight: 900;
    font-size: 32px;
    line-height: 38px;
    color: #fff;
  }

  .divider {
    margin: 30px 0;
    border-color: rgba(#fff, 0.2);
  }

  .modal__close {
    top: 3px;
    right: -25px;
    color: #fff;
    background: transparent;
    border: none;
    cursor: pointer;
    outline: none;
  }

  @include breakpoint("sm-and-down") {
    padding: 20px;
    .divider {
      margin: 20px 0;
    }
  }

  @include breakpoint("xs-only") {
    padding: 16px;
    .divider {
      margin: 16px 0;
    }
    .title {
      font-size: 26px;
    }
  }
}
</style>
